import { useEffect, useState } from "react";
import { toast } from "sonner";
import { apiAdmin } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Save, Plus, Pencil, Trash2, Palette, Type, Sparkles, Image as ImageIcon } from "lucide-react";
import CategoryIcon from "@/components/CategoryIcon";
import InvitationUploader from "@/components/InvitationUploader";

// Available FA icons that admin can choose from (curated)
const AVAILABLE_ICONS = [
  { label: "Gelin", value: "fa-solid fa-person-dress" },
  { label: "Damat", value: "fa-solid fa-user-tie" },
  { label: "Yüzük", value: "fa-solid fa-ring" },
  { label: "Çocuk", value: "fa-solid fa-child-reaching" },
  { label: "Bebek", value: "fa-solid fa-baby" },
  { label: "Kalp", value: "fa-solid fa-heart" },
  { label: "Yıldız", value: "fa-solid fa-star" },
  { label: "Alev", value: "fa-solid fa-fire" },
  { label: "El", value: "fa-solid fa-hand-holding-heart" },
  { label: "Kişi", value: "fa-solid fa-person" },
  { label: "Grup", value: "fa-solid fa-user-group" },
  { label: "Müzik", value: "fa-solid fa-music" },
  { label: "Çiçek", value: "fa-solid fa-seedling" },
  { label: "Hediye", value: "fa-solid fa-gift" },
];

const slugify = (s) =>
  (s || "")
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i").replace(/ö/g, "o").replace(/ü/g, "u")
    .replace(/ş/g, "s").replace(/ç/g, "c").replace(/ğ/g, "g")
    .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 30);

const emptyCat = {
  value: "",
  label: "",
  accent: "#8A252C",
  icons: [],
  hint: "",
  order: 0,
};

const AdminSettings = () => {
  const { categories, settings, refresh } = useSettings();

  // Hero form
  const [hero, setHero] = useState(settings);
  const [savingHero, setSavingHero] = useState(false);

  // Category dialog state
  const [editing, setEditing] = useState(null); // null | { …cat, isNew }

  useEffect(() => { setHero(settings); }, [settings]);

  const saveHero = async () => {
    setSavingHero(true);
    try {
      await apiAdmin.put("/settings", hero);
      await refresh();
      toast.success("Banner kaydedildi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSavingHero(false);
    }
  };

  const startNew = () => setEditing({ ...emptyCat, isNew: true, order: categories.length + 1 });
  const startEdit = (c) => setEditing({ ...c, isNew: false });

  const saveCat = async () => {
    if (!editing) return;
    const payload = {
      value: editing.isNew ? (editing.value || slugify(editing.label)) : undefined,
      label: editing.label.trim(),
      accent: editing.accent || "#8A252C",
      icons: editing.icons || [],
      hint: editing.hint || "",
      order: Number(editing.order) || 0,
    };
    if (!payload.label) {
      toast.error("Kategori adı zorunlu");
      return;
    }
    try {
      if (editing.isNew) {
        if (!payload.value) payload.value = slugify(payload.label);
        await apiAdmin.post("/categories", payload);
        toast.success("Kategori eklendi");
      } else {
        // strip 'value' on update since backend doesn't allow updating value
        const { value: _v, ...rest } = payload;
        await apiAdmin.put(`/categories/${editing.id}`, rest);
        toast.success("Kategori güncellendi");
      }
      await refresh();
      setEditing(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const deleteCat = async (c) => {
    if (!window.confirm(`"${c.label}" kategorisini silmek istiyor musunuz?`)) return;
    try {
      await apiAdmin.delete(`/categories/${c.id}`);
      toast.success("Kategori silindi");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    }
  };

  const toggleIcon = (iconClass) => {
    setEditing((e) => {
      const has = e.icons?.includes(iconClass);
      const next = has ? e.icons.filter((x) => x !== iconClass) : [...(e.icons || []), iconClass];
      return { ...e, icons: next };
    });
  };

  return (
    <div className="space-y-8" data-testid="admin-settings">
      {/* Site identity settings */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-[#8A252C]/10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-[#8A252C]" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">Site Kimliği</div>
            <h3 className="font-display text-xl font-bold text-[#1F2937]">Logo, Ad ve Alt Başlık</h3>
            <p className="text-sm text-[#4B5563] mt-0.5">Üst menü ve alt bilgide görünen genel bilgiler.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Logo</Label>
            <p className="text-xs text-[#4B5563] mt-0.5 mb-2">
              Kare, en az 200×200 piksel önerilir. Yüklemezseniz varsayılan simge kullanılır.
            </p>
            <InvitationUploader
              value={hero.logo_url || ""}
              onChange={(url) => setHero({ ...hero, logo_url: url })}
              compact
              title="Logo dosyasını seçin"
              hint="PNG veya JPG · kare önerilir · en fazla 10 MB"
              doneLabel="Logo yüklendi"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Site Adı</Label>
            <Input value={hero.site_name || ""} onChange={(e) => setHero({ ...hero, site_name: e.target.value })}
              placeholder="ŞAHDER" data-testid="site-name-input"
              className="mt-1 bg-stone-50 border-stone-300 h-11" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Alt Başlık</Label>
            <Input value={hero.site_subtitle || ""} onChange={(e) => setHero({ ...hero, site_subtitle: e.target.value })}
              placeholder="Etkinlik Bildirim Sistemi" data-testid="site-subtitle-input"
              className="mt-1 bg-stone-50 border-stone-300 h-11" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Alt Bilgi Metni</Label>
            <Input value={hero.footer_text || ""} onChange={(e) => setHero({ ...hero, footer_text: e.target.value })}
              placeholder="© ŞAHDER — ..." data-testid="footer-text-input"
              className="mt-1 bg-stone-50 border-stone-300 h-11" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Alt Bilgi Slogan</Label>
            <Input value={hero.footer_tagline || ""} onChange={(e) => setHero({ ...hero, footer_tagline: e.target.value })}
              placeholder="Bir Aradayız, Bir Aradasınız" data-testid="footer-tagline-input"
              className="mt-1 bg-stone-50 border-stone-300 h-11" />
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button onClick={saveHero} disabled={savingHero} data-testid="site-save-btn"
            className="bg-[#8A252C] hover:bg-[#6f1d24] text-white gap-2 rounded-xl h-11 px-5">
            <Save className="w-4 h-4" />
            {savingHero ? "Kaydediliyor..." : "Site Bilgilerini Kaydet"}
          </Button>
        </div>
      </div>

      {/* Hero banner settings */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-[#8A252C]/10 flex items-center justify-center">
            <Type className="w-5 h-5 text-[#8A252C]" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">Site Banner</div>
            <h3 className="font-display text-xl font-bold text-[#1F2937]">Başlık ve Açıklama</h3>
            <p className="text-sm text-[#4B5563] mt-0.5">Üye panelinin üst bannerinde görünen metinleri düzenleyin.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Rozet Metni</Label>
            <Input value={hero.hero_kicker || ""} onChange={(e) => setHero({ ...hero, hero_kicker: e.target.value })}
              placeholder="Üye Etkinlik Bildirim" data-testid="hero-kicker-input"
              className="mt-1 bg-stone-50 border-stone-300 h-11" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Vurgulu Kelime (altın renkli)</Label>
            <Input value={hero.hero_highlight || ""} onChange={(e) => setHero({ ...hero, hero_highlight: e.target.value })}
              placeholder="Habercik." data-testid="hero-highlight-input"
              className="mt-1 bg-stone-50 border-stone-300 h-11" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Ana Başlık</Label>
            <Input value={hero.hero_title || ""} onChange={(e) => setHero({ ...hero, hero_title: e.target.value })}
              placeholder="Aileden Bir" data-testid="hero-title-input"
              className="mt-1 bg-stone-50 border-stone-300 h-11" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Açıklama</Label>
            <Textarea rows={3} value={hero.hero_description || ""} onChange={(e) => setHero({ ...hero, hero_description: e.target.value })}
              data-testid="hero-description-input"
              className="mt-1 bg-stone-50 border-stone-300 resize-none" />
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button onClick={saveHero} disabled={savingHero} data-testid="hero-save-btn"
            className="bg-[#8A252C] hover:bg-[#6f1d24] text-white gap-2 rounded-xl h-11 px-5">
            <Save className="w-4 h-4" />
            {savingHero ? "Kaydediliyor..." : "Bannerı Kaydet"}
          </Button>
        </div>
      </div>

      {/* Category management */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#8A252C]/10 flex items-center justify-center">
              <Palette className="w-5 h-5 text-[#8A252C]" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">Kategoriler</div>
              <h3 className="font-display text-xl font-bold text-[#1F2937]">Etkinlik Türleri</h3>
              <p className="text-sm text-[#4B5563] mt-0.5">Yeni tür ekleyin, düzenleyin veya silin.</p>
            </div>
          </div>
          <Button onClick={startNew} data-testid="category-add-btn"
            className="bg-[#8A252C] hover:bg-[#6f1d24] text-white gap-2 rounded-xl h-10">
            <Plus className="w-4 h-4" />
            Yeni
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {categories.map((c) => (
            <div key={c.id} data-testid={`category-row-${c.value}`}
              className="flex items-center gap-3 border border-stone-200 rounded-xl p-3">
              <span className="w-11 h-11 rounded-lg flex items-center justify-center text-lg"
                style={{ backgroundColor: `${c.accent}15`, color: c.accent }}>
                <CategoryIcon icons={c.icons} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-[#1F2937] leading-tight">{c.label}</div>
                <div className="text-xs text-[#4B5563] truncate">{c.hint || c.value}</div>
              </div>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={() => startEdit(c)}
                  data-testid={`category-edit-${c.value}`}
                  className="border-stone-300 h-8 w-8 p-0" title="Düzenle">
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => deleteCat(c)}
                  data-testid={`category-delete-${c.value}`}
                  className="border-stone-300 h-8 w-8 p-0 text-[#7F1D1D] hover:bg-[#7F1D1D]/5" title="Sil">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit/Add category dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg bg-[#F9F6F0] border-stone-200 max-h-[90vh] overflow-y-auto" data-testid="category-dialog">
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-2xl text-[#1F2937]">
              {editing?.isNew ? "Yeni Kategori" : "Kategori Düzenle"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Kategori Adı</Label>
              <Input value={editing?.label || ""} onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                placeholder="Örn: Doğum Günü" data-testid="cat-label-input"
                className="mt-1 bg-white border-stone-300 h-11" />
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Kısa Açıklama</Label>
              <Input value={editing?.hint || ""} onChange={(e) => setEditing({ ...editing, hint: e.target.value })}
                placeholder="Örn: Yaş kutlaması" data-testid="cat-hint-input"
                className="mt-1 bg-white border-stone-300 h-11" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Renk</Label>
                <div className="mt-1 flex items-center gap-2">
                  <input type="color" value={editing?.accent || "#8A252C"} onChange={(e) => setEditing({ ...editing, accent: e.target.value })}
                    data-testid="cat-accent-input"
                    className="w-12 h-11 rounded-lg border-2 border-stone-200 cursor-pointer" />
                  <Input value={editing?.accent || ""} onChange={(e) => setEditing({ ...editing, accent: e.target.value })}
                    className="bg-white border-stone-300 h-11" />
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Sıra</Label>
                <Input type="number" value={editing?.order || 0} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })}
                  className="mt-1 bg-white border-stone-300 h-11" />
              </div>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">İkonlar (birden fazla seçebilirsiniz)</Label>
              <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
                {AVAILABLE_ICONS.map((ic) => {
                  const active = editing?.icons?.includes(ic.value);
                  return (
                    <button key={ic.value} type="button" onClick={() => toggleIcon(ic.value)}
                      data-testid={`cat-icon-${ic.label}`}
                      className={`flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition ${
                        active ? "border-[#8A252C] bg-[#8A252C]/5" : "border-stone-200 bg-white hover:border-stone-300"
                      }`}>
                      <span className="w-8 h-8 rounded-md flex items-center justify-center text-base"
                        style={{ color: editing?.accent || "#8A252C" }}>
                        <i className={ic.value} aria-hidden="true" />
                      </span>
                      <span className="text-[10px] text-[#4B5563]">{ic.label}</span>
                    </button>
                  );
                })}
              </div>
              {editing?.icons?.length > 0 && (
                <div className="mt-2 flex items-center gap-2 p-2 rounded-lg bg-white border border-stone-200">
                  <span className="text-xs text-[#4B5563]">Önizleme:</span>
                  <span className="w-9 h-9 rounded-lg flex items-center justify-center text-base"
                    style={{ backgroundColor: `${editing.accent}15`, color: editing.accent }}>
                    <CategoryIcon icons={editing.icons} />
                  </span>
                  <span className="text-sm font-semibold text-[#1F2937]">{editing.label || "Örnek Kategori"}</span>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditing(null)} className="border-stone-300 h-11" data-testid="cat-cancel-btn">
              Vazgeç
            </Button>
            <Button onClick={saveCat} className="bg-[#8A252C] hover:bg-[#6f1d24] text-white h-11 gap-2" data-testid="cat-save-btn">
              <Save className="w-4 h-4" />
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSettings;
